import { UsersIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { KlassConectaTag } from '@/components/ui/custom/ConectaTag';
import Tag from '@/components/ui/custom/dataDisplay/Tag';
import Text from '@/components/ui/custom/dataDisplay/Text';
import Skeleton from '@/components/ui/custom/Skeleton';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { coursePathsQueryKeys } from '@/data/services/querykeys';
import Klass from '@/models/Klass';
import { getLineColorClass } from '@/utils/courseLineColors';
import { ROUTES } from '@/utils/routes';
import { isFinishedKlass, isLastTwoWeeksLesson } from '@/utils/teachingLesson';
import KlassPopup from '../KlassPopup/KlassPopup';
import NextLessonKlass from '../NextLessonKlass';
import { TeachingWeekdayProps } from './TeachingWeekday';

export type WeekdayKlassProps = Partial<TeachingWeekdayProps> & {
  klass: Klass;
};

export default function WeekdayKlassCell({
  klass,
  onChangeModal,
}: WeekdayKlassProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'weekdayKlassCell',
  });

  const { data: coursePath, isInitialLoading: loading } = useQuery({
    ...coursePathsQueryKeys.get(klass.coursePathSlug),
    enabled: !!klass,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const endDate = moment(klass.klassEndDate).format('DD/MM');

  const lastTwoWeeksLesson = isLastTwoWeeksLesson(klass.klassEndDate);

  const finishedKlass = isFinishedKlass(klass.klassEndDate);

  const to = ROUTES.ADMIN.CLASSES.INFO({
    name: klass.name.replace('#', ''),
  });

  if (loading)
    return (
      <Skeleton
        testId="loading"
        className="p-2.5 min-h-40 max-h-40 w-full rounded-xl bg-primary-content"
      />
    );

  const lineName = coursePath?.course?.module?.courseLine?.name;

  if (lineName) {
    return (
      <li
        data-testid="weekdayKlassCell"
        className={`flex flex-col rounded-xl text-primary p-2.5 gap-1 min-h-40 max-h-40 justify-between overflow-x-hidden w-full scrollbar-none ${getLineColorClass(
          lineName,
        )}`}
      >
        <div className="inline-flex">
          <Text
            format="rubik-700"
            text={klass.name}
            href={to}
            className="text-14 2xl:text-16"
          />

          <KlassConectaTag klassType={klass.klassType} />
        </div>

        <Tag
          testId="endDate"
          className={{
            base: `flex justify-center py-1 px-2.5 rounded-xl gap-1 self-center ${
              lastTwoWeeksLesson ? 'bg-info text-base-100' : 'text-base-content'
            }`,
          }}
          color="custom"
        >
          <Text
            format="rubik-500"
            text={`${t('termination')}: `}
            className="text-14 2xl:text-16"
          />
          <Text
            format="rubik-400"
            text={endDate}
            className="text-14 2xl:text-16"
          />
        </Tag>

        <NextLessonKlass
          klassId={finishedKlass ? undefined : klass.id}
          courseSlug={coursePath.slug}
        />

        <section className="flex w-full justify-between gap-1 text-center">
          <div className="flex gap-0.5 items-center">
            <Text format="rubik-700" text={klass.enrollsCount} />
            <UsersIcon className="w-3.5" strokeWidth={3} />
          </div>
          <KlassPopup klass={klass} onChangeModal={onChangeModal} />
        </section>
      </li>
    );
  }

  return null;
}
